import {
  ReactNode,
  useCallback,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {useLocation} from "react-router-dom";
import {useSelector} from "react-redux";

import {
  readLocalStorage,
  removeLocalStorage,
  saveLocalStorage,
} from "hooks/useLocalStorage";
import useAuthSession from "hooks/useAuthSession";
import useToast from "hooks/useToast";
import {PROCESS_DETAILS_ROUTE} from "global/constants";
import {KeyCloakRefreshTokenAuth} from "services/api_v2/Auth/auth.service";
import {
  NIAMessage,
  NIAOriginContext,
  NIAPayload,
  NIAServiceError,
  NIATheme,
  getNIAEndpoint,
  normalizeNIAResponse,
  postNIAMessage,
  readNIAResponse,
} from "services/NIA/nia.service";

type NIAThemeOption = {
  id: NIATheme;
  label: string;
  description: string;
};

type NIAThemeSuggestion = {
  id: string;
  title: string;
  description: string;
  prompt: string;
};

type NIAProcessPageContext = {
  processId?: number | string;
  processNumber?: string | null;
};

type NIAContextValue = {
  currentMessages: NIAMessage[];
  currentSessionId: string;
  isDrawerOpen: boolean;
  originContext: NIAOriginContext;
  selectedTheme: NIATheme | null;
  sending: boolean;
  themeOptions: NIAThemeOption[];
  suggestions: NIAThemeSuggestion[];
  hasProcessContext: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  setSelectedTheme: (theme: NIATheme) => void;
  resetCurrentConversation: () => void;
  clearSelectedTheme: () => void;
  sendMessage: (content: string) => Promise<boolean>;
  cancelMessage: () => void;
  registerProcessPageContext: (context: NIAProcessPageContext) => void;
  clearProcessPageContext: () => void;
};

const SESSION_TTL_MS = 30 * 60 * 1000;
const THEME_STORAGE_KEY = "nia.selectedTheme";
const SESSION_STORAGE_PREFIX = "nia.sessionId.";

const THEME_OPTIONS: NIAThemeOption[] = [
  {
    id: "im",
    label: "IM",
    description: "Interações com foco em Investimentos e Mercado.",
  },
];

const THEME_SUGGESTIONS: Record<NIATheme, NIAThemeSuggestion[]> = {
  im: [
    {
      id: "im-ocorrencias-opa",
      title: "Ocorrências e OPA",
      description: "Entenda o tratamento de imprevistos com transparência.",
      prompt:
        "Saiba o que ocorreu, por que ocorreu e o que estamos fazendo para resolver quando registro uma ocorrência ou aciono a OPA.",
    },
    {
      id: "im-regulamento-aduaneiro",
      title: "Regulamento Aduaneiro",
      description: "Pontos essenciais do Decreto 6.759/2009.",
      prompt:
        "Quais são as regras do Regulamento Aduaneiro (Dec. 6.759/2009) que mais impactam o desembaraço e as etapas da importação?",
    },
    {
      id: "im-seguros-averbacao",
      title: "Seguros e averbação provisória",
      description: "Quando usar a cobertura e como solicitar a averbação.",
      prompt:
        "Explique como funcionam nossos seguros, quando recorrer à averbação provisória e quais documentos preciso apresentar.",
    },
    {
      id: "im-ce-mercante",
      title: "Liberação da CE Mercante",
      description: "Entenda o fluxo para liberar sem atrasos.",
      prompt:
        "Detalhe o processo de liberação da CE Mercante, quais prazos observar e como acompanhar os passos no sistema.",
    },
  ],
};

const NIAContext = createContext<NIAContextValue | null>(null);

const generateId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const isValidTheme = (value: unknown): value is NIATheme => {
  return value === "im";
};

const getSessionStorageKey = (theme: NIATheme) => {
  return `${SESSION_STORAGE_PREFIX}${theme}`;
};

const readThemeSession = (theme: NIATheme) => {
  return readLocalStorage<string>(getSessionStorageKey(theme));
};

const persistThemeSession = (theme: NIATheme, sessionId: string) => {
  saveLocalStorage(getSessionStorageKey(theme), sessionId);
};

const createThemeSession = (theme: NIATheme) => {
  const sessionId = generateId();
  persistThemeSession(theme, sessionId);
  return sessionId;
};

const ensureThemeSession = (theme: NIATheme) => {
  return readThemeSession(theme) ?? createThemeSession(theme);
};

const createEmptyThemeMessages = () => {
  return {
    im: [] as NIAMessage[],
  };
};

const inferThemeFromProcessNumber = (
  processNumber?: string | null,
): NIATheme | null => {
  const normalizedValue = processNumber?.trim().toUpperCase();

  if (!normalizedValue) return null;
  if (normalizedValue.includes("NEXTIM")) return "im";

  return null;
};

const getStoredTheme = () => {
  const storedTheme = readLocalStorage<string>(THEME_STORAGE_KEY);
  return isValidTheme(storedTheme) ? storedTheme : null;
};

const clearStoredTheme = () => {
  removeLocalStorage(THEME_STORAGE_KEY);
};

type Props = {
  children: ReactNode;
};

const NIAProvider = ({children}: Props) => {
  const location = useLocation();
  const [showToast] = useToast();
  const {setAuthSession} = useAuthSession();

  const {userInfo} = useSelector((state: any) => state.stateUserInfo);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTheme, setSelectedThemeState] = useState<NIATheme | null>(
    () => getStoredTheme() ?? "im",
  );
  const [sessionsByTheme, setSessionsByTheme] = useState<Record<NIATheme, string>>(
    () => ({
      im: ensureThemeSession("im"),
    }),
  );
  const [messagesByTheme, setMessagesByTheme] = useState<
    Record<NIATheme, NIAMessage[]>
  >(() => createEmptyThemeMessages());
  const [lastActivityAtByTheme, setLastActivityAtByTheme] = useState<
    Record<NIATheme, number>
  >(() => ({
    im: Date.now(),
  }));
  const [sending, setSending] = useState(false);
  const [pageProcessContext, setPageProcessContext] =
    useState<NIAProcessPageContext | null>(null);
  const [originContext, setOriginContext] = useState<NIAOriginContext>(() => ({
    originRoute: location.pathname,
  }));

  const abortRef = useRef<AbortController | null>(null);

  const persistSelectedTheme = useCallback((theme: NIATheme) => {
    saveLocalStorage(THEME_STORAGE_KEY, theme);
    setSelectedThemeState(theme);
  }, []);

  const applyThemeSelection = useCallback((theme: NIATheme | null) => {
    if (!theme) return;
    persistSelectedTheme(theme);
  }, [persistSelectedTheme]);

  const resolveContextFromCurrentPage = useCallback((): NIAOriginContext => {
    const isProcessDetailsRoute = location.pathname.startsWith(
      `/${PROCESS_DETAILS_ROUTE}/`,
    );

    if (isProcessDetailsRoute && pageProcessContext?.processId) {
      const inferredTheme = inferThemeFromProcessNumber(
        pageProcessContext.processNumber,
      );

      return {
        originRoute: location.pathname,
        processId: pageProcessContext.processId,
        processNumber: pageProcessContext.processNumber ?? undefined,
        theme: inferredTheme,
      };
    }

    return {
      originRoute: location.pathname,
      theme: selectedTheme,
    };
  }, [location.pathname, pageProcessContext, selectedTheme]);

  const openDrawer = useCallback(() => {
    const nextContext = resolveContextFromCurrentPage();
    setOriginContext(nextContext);

    if (nextContext.theme) {
      applyThemeSelection(nextContext.theme);
    }

    setIsDrawerOpen(true);
  }, [applyThemeSelection, resolveContextFromCurrentPage]);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const resetCurrentConversation = useCallback(() => {
    if (!selectedTheme) return;

    const nextSessionId = createThemeSession(selectedTheme);

    setMessagesByTheme((currentValue) => ({
      ...currentValue,
      [selectedTheme]: [],
    }));
    setSessionsByTheme((currentValue) => ({
      ...currentValue,
      [selectedTheme]: nextSessionId,
    }));
    setLastActivityAtByTheme((currentValue) => ({
      ...currentValue,
      [selectedTheme]: Date.now(),
    }));
  }, [selectedTheme]);

  const clearSelectedTheme = useCallback(() => {
    clearStoredTheme();
    setSelectedThemeState("im");
  }, []);

  const refreshSessionAndRetry = useCallback(
    async (
      payload: NIAPayload,
      theme: NIATheme,
      controller: AbortController,
    ) => {
      const refreshedSession = await KeyCloakRefreshTokenAuth(
        userInfo?.refresh_token ?? "",
      );

      setAuthSession(refreshedSession.data);

      const retryResponse = await postNIAMessage({
        accessToken: refreshedSession.data.access_token,
        endpoint: getNIAEndpoint(theme),
        payload,
        signal: controller.signal,
      });

      return retryResponse;
    },
    [setAuthSession, userInfo?.refresh_token],
  );

  const sendMessage = useCallback(async (content: string) => {
    const messageContent = content.trim();

    if (!messageContent || !selectedTheme || sending) {
      return false;
    }

    const endpoint = getNIAEndpoint(selectedTheme).trim();

    if (!endpoint) {
      showToast("Endpoint da NIA não está configurado.", "error");
      return false;
    }

    const now = Date.now();
    let activeSessionId = sessionsByTheme[selectedTheme];

    if (now - lastActivityAtByTheme[selectedTheme] > SESSION_TTL_MS) {
      activeSessionId = createThemeSession(selectedTheme);
      setSessionsByTheme((currentValue) => ({
        ...currentValue,
        [selectedTheme]: activeSessionId,
      }));
    }

    const userMessage: NIAMessage = {
      id: generateId(),
      role: "user",
      content: messageContent,
      createdAt: now,
    };

    const currentThemeHistory = messagesByTheme[selectedTheme] ?? [];
    const nextMessages = currentThemeHistory.concat(userMessage);
    const payload: NIAPayload = {
      chatInput: messageContent,
      sessionId: activeSessionId,
      theme: selectedTheme,
      messages: nextMessages.map(({role, content}) => ({
        role,
        content,
      })),
      originContext: {
        ...originContext,
        theme: selectedTheme,
      },
      processId: originContext.processId,
      processNumber: originContext.processNumber,
    };

    setMessagesByTheme((currentValue) => ({
      ...currentValue,
      [selectedTheme]: nextMessages,
    }));
    setLastActivityAtByTheme((currentValue) => ({
      ...currentValue,
      [selectedTheme]: now,
    }));
    setSending(true);

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await postNIAMessage({
        accessToken: userInfo?.access_token ?? "",
        endpoint,
        payload,
        signal: controller.signal,
      });

      const responseText = await readNIAResponse(response);
      const normalizedResponse =
        responseText.trim().length > 0
          ? normalizeNIAResponse(responseText)
          : "Resposta vazia do servidor.";

      const assistantMessage: NIAMessage = {
        id: generateId(),
        role: "assistant",
        content: normalizedResponse,
        createdAt: Date.now(),
      };

      setMessagesByTheme((currentValue) => ({
        ...currentValue,
        [selectedTheme]: currentValue[selectedTheme].concat(assistantMessage),
      }));
      setLastActivityAtByTheme((currentValue) => ({
        ...currentValue,
        [selectedTheme]: Date.now(),
      }));

      return true;
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === "AbortError"
      ) {
        return false;
      }

      if (
        error instanceof NIAServiceError &&
        error.status === 401 &&
        typeof userInfo?.refresh_token === "string" &&
        userInfo.refresh_token.length > 0
      ) {
        try {
          const response = await refreshSessionAndRetry(
            payload,
            selectedTheme,
            controller,
          );

          const responseText = await readNIAResponse(response);
          const normalizedResponse =
            responseText.trim().length > 0
              ? normalizeNIAResponse(responseText)
              : "Resposta vazia do servidor.";

          const assistantMessage: NIAMessage = {
            id: generateId(),
            role: "assistant",
            content: normalizedResponse,
            createdAt: Date.now(),
          };

          setMessagesByTheme((currentValue) => ({
            ...currentValue,
            [selectedTheme]: currentValue[selectedTheme].concat(
              assistantMessage,
            ),
          }));
          setLastActivityAtByTheme((currentValue) => ({
            ...currentValue,
            [selectedTheme]: Date.now(),
          }));

          return true;
        } catch (refreshError) {
          const message =
            refreshError instanceof Error
              ? refreshError.message
              : "Falha ao renovar a sessão da NIA.";

          showToast(message, "error");
        }
      } else {
        const message =
          error instanceof Error
            ? error.message
            : "Falha ao enviar mensagem para a NIA.";

        showToast(message, "error");
      }

      return false;
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }, [
    lastActivityAtByTheme,
    messagesByTheme,
    originContext,
    refreshSessionAndRetry,
    selectedTheme,
    sending,
    sessionsByTheme,
    showToast,
    userInfo?.access_token,
    userInfo?.refresh_token,
  ]);

  const cancelMessage = useCallback(() => {
    if (!abortRef.current) return;

    abortRef.current.abort();
    abortRef.current = null;
    setSending(false);
  }, []);

  const registerProcessPageContext = useCallback(
    (context: NIAProcessPageContext) => {
      setPageProcessContext(context);
    },
    [],
  );

  const clearProcessPageContext = useCallback(() => {
    setPageProcessContext(null);
  }, []);

  const handleThemeSelection = useCallback((theme: NIATheme) => {
    applyThemeSelection(theme);

    if (originContext.processId) {
      setOriginContext((currentValue) => ({
        ...currentValue,
        theme,
      }));
    }
  }, [applyThemeSelection, originContext.processId]);

  const currentMessages = selectedTheme ? messagesByTheme[selectedTheme] : [];
  const currentSessionId = selectedTheme
    ? sessionsByTheme[selectedTheme]
    : "";
  const suggestions = selectedTheme ? THEME_SUGGESTIONS[selectedTheme] : [];

  const value = useMemo<NIAContextValue>(
    () => ({
      currentMessages,
      currentSessionId,
      isDrawerOpen,
      originContext,
      selectedTheme,
      sending,
      themeOptions: THEME_OPTIONS,
      suggestions,
      hasProcessContext: Boolean(originContext.processId),
      openDrawer,
      closeDrawer,
      setSelectedTheme: handleThemeSelection,
      resetCurrentConversation,
      clearSelectedTheme,
      sendMessage,
      cancelMessage,
      registerProcessPageContext,
      clearProcessPageContext,
    }),
    [
      cancelMessage,
      clearProcessPageContext,
      clearSelectedTheme,
      closeDrawer,
      currentMessages,
      currentSessionId,
      handleThemeSelection,
      isDrawerOpen,
      originContext,
      openDrawer,
      registerProcessPageContext,
      resetCurrentConversation,
      selectedTheme,
      sendMessage,
      sending,
      suggestions,
    ],
  );

  return <NIAContext.Provider value={value}>{children}</NIAContext.Provider>;
};

export const useNIA = () => {
  const context = useContext(NIAContext);

  if (!context) {
    throw new Error("useNIA must be used within NIAProvider");
  }

  return context;
};

export default NIAProvider;
